/**
 * RoomRecognitionManager
 *
 * Main container component for managing room recognition.
 * Allows adding rooms with reference photos, viewing existing rooms,
 * and testing recognition.
 */

import React, { useState, useCallback } from 'react';
import {
  Camera,
  Plus,
  Trash2,
  Image as ImageIcon,
  TestTube,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import { useRoomRecognition } from '../hooks/useRoomRecognition';
import RoomEditor from './RoomEditor';
import RoomTestPanel from './RoomTestPanel';
import toast from 'react-hot-toast';

/**
 * RoomRecognitionManager Component
 *
 * @param {Object} props
 * @param {string} props.guideId - The guide/museum ID
 * @param {string} props.guideName - The guide/museum name
 * @param {Array} props.stops - List of stops in the guide (for associating rooms with stops)
 * @param {boolean} props.isEnabled - Whether room recognition is enabled for this guide
 * @param {Function} props.onToggleEnabled - Callback to toggle room recognition
 */
const RoomRecognitionManager = ({
  guideId,
  guideName,
  stops = [],
  isEnabled = false,
  onToggleEnabled
}) => {
  // State
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [isTestPanelOpen, setIsTestPanelOpen] = useState(false);
  const [expandedRoom, setExpandedRoom] = useState(null);

  // Hook
  const {
    rooms,
    isLoading,
    isSaving,
    error,
    roomCount,
    hasRooms,
    testResult,
    createRoom,
    addRoomImages,
    removeRoom,
    testRoomRecognition,
    clearTestResult,
    refreshServerCache,
    clearError
  } = useRoomRecognition(guideId, guideName);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddRoom = useCallback(() => {
    setEditingRoom(null);
    setIsEditorOpen(true);
  }, []);

  const handleEditRoom = useCallback((room) => {
    setEditingRoom(room);
    setIsEditorOpen(true);
  }, []);

  const handleCloseEditor = useCallback(() => {
    setIsEditorOpen(false);
    setEditingRoom(null);
  }, []);

  const handleSaveRoom = useCallback(async (roomData, imageFiles) => {
    try {
      if (editingRoom) {
        // Adding more images to existing room
        await addRoomImages(editingRoom.roomId, imageFiles);
        toast.success(`Added ${imageFiles.length} images to "${roomData.roomName}"`);
      } else {
        // Creating new room
        await createRoom(roomData, imageFiles);
        toast.success(`Room "${roomData.roomName}" created with ${imageFiles.length} reference images`);
      }
      handleCloseEditor();
    } catch (err) {
      toast.error(err.message || 'Failed to save room');
    }
  }, [editingRoom, createRoom, addRoomImages, handleCloseEditor]);

  const handleDeleteRoom = useCallback(async (room) => {
    if (!window.confirm(`Delete room "${room.roomName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await removeRoom(room.roomId);
      toast.success(`Room "${room.roomName}" deleted`);
    } catch (err) {
      toast.error(err.message || 'Failed to delete room');
    }
  }, [removeRoom]);

  const handleTestRecognition = useCallback(async (imageFile) => {
    try {
      const result = await testRoomRecognition(imageFile);
      if (result.match) {
        toast.success(`Matched: ${result.match.roomName} (${Math.round(result.confidence * 100)}%)`);
      } else {
        toast.error(`No match found. Closest: ${result.closestRoom?.roomName || 'none'}`);
      }
    } catch (err) {
      toast.error(err.message || 'Recognition test failed');
    }
  }, [testRoomRecognition]);

  const handleRefreshCache = useCallback(async () => {
    try {
      await refreshServerCache();
      toast.success('Cache refreshed');
    } catch (err) {
      toast.error('Failed to refresh cache');
    }
  }, [refreshServerCache]);

  const toggleRoomExpanded = useCallback((roomId) => {
    setExpandedRoom(prev => prev === roomId ? null : roomId);
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Camera className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Room Recognition</h2>
            <p className="text-sm text-gray-500">
              Enable AI-powered indoor room identification for this guide
            </p>
          </div>
        </div>

        {/* Enable Toggle */}
        <label className="flex items-center gap-3 cursor-pointer">
          <span className="text-sm font-medium text-gray-700">
            {isEnabled ? 'Enabled' : 'Disabled'}
          </span>
          <div className="relative">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => onToggleEnabled?.(e.target.checked)}
              className="sr-only"
            />
            <div className={`w-11 h-6 rounded-full transition-colors ${
              isEnabled ? 'bg-purple-600' : 'bg-gray-300'
            }`}>
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                isEnabled ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </div>
          </div>
        </label>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={clearError}
            className="text-red-500 hover:text-red-700"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {isEnabled && (
        <>
          {/* Stats Bar */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-2xl font-bold text-purple-700">{roomCount}</p>
                  <p className="text-xs text-purple-600">Registered Rooms</p>
                </div>
                <div className="h-8 w-px bg-purple-200" />
                <div>
                  <p className="text-sm text-gray-600">
                    {hasRooms
                      ? 'Users can scan rooms to trigger audio'
                      : 'Add rooms to enable indoor recognition'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefreshCache}
                  className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                  title="Refresh server cache"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIsTestPanelOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                >
                  <TestTube className="w-4 h-4" />
                  Test Recognition
                </button>
                <button
                  onClick={handleAddRoom}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Room
                </button>
              </div>
            </div>
          </div>

          {/* Rooms List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent" />
            </div>
          ) : hasRooms ? (
            <div className="space-y-3">
              {rooms.map((room) => (
                <RoomCard
                  key={room.roomId}
                  room={room}
                  isExpanded={expandedRoom === room.roomId}
                  onToggleExpand={() => toggleRoomExpanded(room.roomId)}
                  onEdit={() => handleEditRoom(room)}
                  onDelete={() => handleDeleteRoom(room)}
                  stops={stops}
                />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <Camera className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Rooms Registered</h3>
              <p className="text-gray-500 mb-4">
                Add rooms with reference photos to enable indoor recognition for this guide.
              </p>
              <button
                onClick={handleAddRoom}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add First Room
              </button>
            </div>
          )}
        </>
      )}

      {!isEnabled && (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <Camera className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Room Recognition Disabled</h3>
          <p className="text-gray-500">
            Enable room recognition to allow users to scan rooms with their camera
            and automatically trigger the corresponding audio guide.
          </p>
        </div>
      )}

      {/* Room Editor Modal */}
      {isEditorOpen && (
        <RoomEditor
          room={editingRoom}
          stops={stops}
          guideId={guideId}
          guideName={guideName}
          onSave={handleSaveRoom}
          onClose={handleCloseEditor}
          isSaving={isSaving}
        />
      )}

      {/* Test Panel Modal */}
      {isTestPanelOpen && (
        <RoomTestPanel
          guideId={guideId}
          guideName={guideName}
          testResult={testResult}
          isLoading={isLoading}
          onTest={handleTestRecognition}
          onClearResult={clearTestResult}
          onClose={() => setIsTestPanelOpen(false)}
        />
      )}
    </div>
  );
};

/**
 * RoomCard Component
 */
const RoomCard = ({ room, isExpanded, onToggleExpand, onEdit, onDelete, stops }) => {
  // Find associated stop
  const associatedStop = stops.find(s => s.id === room.stopId || s.stopId === room.stopId);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Eye className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{room.roomName}</h3>
            <p className="text-sm text-gray-500">
              {room.referenceCount || 0} reference images
              {associatedStop && ` • Linked to: ${associatedStop.name || associatedStop.title}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full ${
            room.referenceCount >= 3
              ? 'bg-green-100 text-green-700'
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {room.referenceCount >= 3 ? 'Ready' : 'Needs more images'}
          </span>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Room ID</p>
              <p className="text-sm font-mono text-gray-700">{room.roomId}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Audio Guide ID</p>
              <p className="text-sm font-mono text-gray-700">{room.audioGuideId || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Stop ID</p>
              <p className="text-sm font-mono text-gray-700">{room.stopId || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Confidence Threshold</p>
              <p className="text-sm text-gray-700">{Math.round((room.confidenceThreshold || 0.7) * 100)}%</p>
            </div>
          </div>

          {/* Reference Images Preview */}
          {room.metadata?.imageUrls && room.metadata.imageUrls.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Reference Images</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {room.metadata.imageUrls.slice(0, 5).map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`Reference ${i + 1}`}
                    className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                  />
                ))}
                {room.metadata.imageUrls.length > 5 && (
                  <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center text-sm text-gray-500">
                    +{room.metadata.imageUrls.length - 5}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
            >
              <ImageIcon className="w-4 h-4" />
              Add More Images
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomRecognitionManager;
